import React from "react";
import {
  X,
  Users,
  Calendar,
  ShieldAlert,
  Database,
  Sparkles,
} from "lucide-react";

const formatNum = (n) => (n || 0).toLocaleString();
const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "");

const severityColor = {
  critical:
    "bg-red-500/10 text-red-400 border-red-500/30 shadow-[0_0_12px_rgba(239,68,68,0.25)]",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/30 shadow-[0_0_12px_rgba(249,115,22,0.2)]",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  low: "bg-sky-500/10 text-sky-400 border-sky-500/30",
};

const BreachDetailModal = ({ breach, onClose }) => {
  if (!breach) return null;

  const description =
    stripHtml(breach.description) ||
    `In ${
      breach.breachDate ? new Date(breach.breachDate).getFullYear() : "the past"
    }, ${breach.title} experienced a data breach. Affected accounts and exposed data are listed below.`;

  const hasPasswords = (breach.dataClasses || []).some((c) =>
    /password/i.test(c),
  );

  const aiDescription = `The exposed data from the ${breach.title} breach could facilitate phishing, credential stuffing, and identity theft. ${
    hasPasswords
      ? "Because passwords were exposed, all affected users should rotate credentials immediately and enable multi-factor authentication."
      : "Affected users should remain vigilant for targeted phishing attempts using the exposed information."
  }`;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-[#12151C] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 border border-cyan-500/20 shadow-[0_0_40px_rgba(6,182,212,0.15)]"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundImage:
            "radial-gradient(circle at top right, rgba(6,182,212,0.08), transparent 40%), radial-gradient(circle at bottom left, rgba(139,92,246,0.06), transparent 40%)",
        }}
      >
        {/* Top corner accents */}
        <div className="absolute top-0 left-0 w-12 h-12 border-t border-l border-cyan-400/40 rounded-tl-2xl pointer-events-none" />
        <div className="absolute top-0 right-0 w-12 h-12 border-t border-r border-cyan-400/40 rounded-tr-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-12 h-12 border-b border-l border-cyan-400/40 rounded-bl-2xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-12 h-12 border-b border-r border-cyan-400/40 rounded-br-2xl pointer-events-none" />

        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1.5 hover:bg-white/5 rounded-full transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} className="text-gray-400 hover:text-cyan-400" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#1a1f2a] to-[#0f1218] border border-cyan-500/20 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-[inset_0_0_20px_rgba(6,182,212,0.1)]">
            {breach.logoPath ? (
              <img
                src={breach.logoPath}
                alt={breach.title}
                className="w-12 h-12 object-contain"
                onError={(e) => (e.target.style.display = "none")}
              />
            ) : (
              <span className="text-3xl font-bold text-cyan-400/70">
                {breach.title?.[0] || "?"}
              </span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert size={14} className="text-red-400" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-red-400/80 font-mono">
                Breach Record
              </span>
            </div>
            <h2 className="text-2xl font-bold text-cyan-300">{breach.title}</h2>
            <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-1 font-mono">
              <Calendar size={12} />
              {breach.breachDate
                ? new Date(breach.breachDate).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "unknown date"}
            </p>
          </div>
        </div>

        {/* Stats pill */}
        <div className="mb-5 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-cyan-500/10 text-cyan-300 text-xs font-mono border border-cyan-500/20">
            <Users size={13} />
            AFFECTED: {formatNum(breach.pwnCount)}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-violet-500/10 text-violet-300 text-xs font-mono border border-violet-500/20">
            <Database size={13} />
            {(breach.dataClasses || []).length} DATA TYPES
          </span>
        </div>

        {/* Description */}
        <div className="mb-5 p-4 bg-black/30 border border-white/5 rounded-lg">
          <div className="text-[10px] font-mono text-gray-500 mb-2 tracking-widest">
            // INCIDENT SUMMARY
          </div>
          <p className="text-gray-300 leading-relaxed text-sm">{description}</p>
        </div>

        {/* Data Types */}
        <div className="mb-2">
          <h3 className="text-xs font-mono tracking-[0.2em] uppercase text-cyan-400/80 mb-3">
            // Compromised Data Vectors
          </h3>
          {(breach.dataClasses || []).length === 0 ? (
            <p className="text-sm text-gray-500">No data types recorded.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {breach.dataClasses.map((d) => (
                <span
                  key={d}
                  className="px-3 py-1.5 bg-cyan-500/5 text-cyan-300 border border-cyan-500/20 rounded-md text-xs font-mono hover:bg-cyan-500/10 transition-colors"
                >
                  {d}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Severity footer */}
        {breach.severity && (
          <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-center">
            <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-gray-500">
              Threat Level
            </span>
            <span
              className={`text-xs px-3 py-1 rounded-md border font-mono uppercase tracking-wider ${
                severityColor[breach.severity] || ""
              }`}
            >
              {breach.severity}
            </span>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 rounded-md font-mono text-xs tracking-[0.2em] uppercase border border-cyan-500/30 hover:border-cyan-400/60 transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreachDetailModal;
